import { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  parseSearchWith,
  redirect,
  stringifySearchWith,
} from "@tanstack/react-router";
import { ensureAuth, ensureOnboarded, requireAdmin, resolverObraActiva } from "@/app/guards";
import { resolverObraRecordada } from "@/app/obra-activa";
import { AppLayout } from "@/features/app/AppLayout";
import { RootLayout } from "@/features/app/RootLayout";
import { AreasPage } from "@/features/areas/AreasPage";
import { AuditoriaPage } from "@/features/auditoria/AuditoriaPage";
import { ChangePasswordPage } from "@/features/auth/ChangePasswordPage";
import { InvitacionPage } from "@/features/auth/InvitacionPage";
import { LoginPage } from "@/features/auth/LoginPage";
import { NdaPage } from "@/features/auth/NdaPage";
import { RecoverPage } from "@/features/auth/RecoverPage";
import { ResetPasswordPage } from "@/features/auth/ResetPasswordPage";
import { CandadosPage } from "@/features/candados/CandadosPage";
import { DashboardPage } from "@/features/dashboard/DashboardPage";
import { EquiposPage } from "@/features/equipos/EquiposPage";
import { FuentesPage } from "@/features/fuentes/FuentesPage";
import { InformesPage } from "@/features/informes/InformesPage";
import { LotoDetailPage } from "@/features/lotos/LotoDetailPage";
import { LotosPage } from "@/features/lotos/LotosPage";
import { ConfiguracionObraPage } from "@/features/obras/ConfiguracionObraPage";
import { GestionObrasPage } from "@/features/obras/GestionObrasPage";
import { ObraSelectorPage } from "@/features/obras/ObraSelectorPage";
import { ProcedimientosPage } from "@/features/procedimientos/ProcedimientosPage";
import { PuntosPage } from "@/features/puntos/PuntosPage";
import { ConflictCenterPage } from "@/features/sync/ConflictCenterPage";
import { UsuariosPage } from "@/features/usuarios/UsuariosPage";
import { canManageObra } from "@/lib/auth";
import { validadorDeFiltros } from "@/lib/filtros";
import { CLAVES_AREAS } from "@/lib/filtros/areas";
import { CLAVES_CANDADOS } from "@/lib/filtros/candados";
import { CLAVES_EQUIPOS } from "@/lib/filtros/equipos";
import { CLAVES_FUENTES } from "@/lib/filtros/fuentes";
import { CLAVES_LOTOS } from "@/lib/filtros/lotos";
import { CLAVES_PROCEDIMIENTOS } from "@/lib/filtros/procedimientos";
import { CLAVES_PUNTOS } from "@/lib/filtros/puntos";
import { CLAVES_USUARIOS } from "@/lib/filtros/usuarios";
import { leerObraRecordada, recordarObraActiva } from "@/lib/obra-activa";
import { CACHE_MAX_AGE_MS } from "@/lib/offline-cache";

export interface RouterContext {
  queryClient: QueryClient;
}

// Instancia única de Query compartida entre el RouterProvider y los guards.
// gcTime se alinea al TTL de la caché offline (#51): persistQueryClient solo puede
// volcar a IndexedDB las queries que sigan vivas en memoria, así que deben sobrevivir
// al menos tanto como dura la caché persistida.
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: false, refetchOnWindowFocus: false, gcTime: CACHE_MAX_AGE_MS },
  },
});

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
});

// Search tipado de /login: el deep-link a recuperar tras autenticarse (redirect) y
// el código de error del SSO (sso_error) con el que el callback de Microsoft devuelve
// al usuario al login cuando el flujo falla (#91).
function loginSearchSchema(search: Record<string, unknown>): {
  redirect?: string;
  sso_error?: string;
} {
  return {
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
    sso_error: typeof search.sso_error === "string" ? search.sso_error : undefined,
  };
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: loginSearchSchema,
  component: LoginPage,
});

// Recuperación de contraseña autoservicio (#198): rutas PÚBLICAS, sin sesión ni
// onboarding, al mismo nivel que el login. `/recuperar` pide el correo (respuesta
// neutra) y `/restablecer?token=...` consume el token del correo para fijar la nueva
// clave (el enlace lo genera el backend en email/plantilla.go).
const recoverRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/recuperar",
  component: RecoverPage,
});

// Search tipado de /restablecer: el token del enlace del correo.
function restablecerSearchSchema(search: Record<string, unknown>): { token?: string } {
  return { token: typeof search.token === "string" ? search.token : undefined };
}

const resetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/restablecer",
  validateSearch: restablecerSearchSchema,
  component: ResetPasswordPage,
});

// Canje de la invitación de registro (#239): ruta PÚBLICA hermana de `/restablecer`. El
// enlace lo compone el backend en email/plantilla.go con LOTO_BASE_URL; el token viaja en
// la query del SPA (no del API) y de ahí al cuerpo de POST /invitacion/{validar,aceptar}.
const invitacionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invitacion",
  validateSearch: restablecerSearchSchema,
  component: InvitacionPage,
});

// Ruta de layout sin path (id-based): aplica el gate de sesión + onboarding una sola
// vez y renderiza AppLayout (chasis con nav). Las páginas de la app cuelgan de aquí.
const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: async ({ context, location }) => {
    const me = await ensureAuth(context.queryClient, location.href);
    ensureOnboarded(me); // clave → NDA → app
    return { me };
  },
  component: AppLayout,
});

// La raíz recupera el trabajo donde se dejó (#252): entra en la obra recordada si sigue
// siendo válida (miembro de ella, D8) y, si no la hay, cae al selector de obras, que
// auto-entra cuando el usuario tiene una sola. La app no tiene dashboard global, sino uno
// por obra (`/obras/$obraId`).
const indexRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/",
  beforeLoad: ({ context }) => {
    const recordada = resolverObraRecordada(context.me, leerObraRecordada());
    if (recordada !== undefined) {
      throw redirect({ to: "/obras/$obraId", params: { obraId: String(recordada) } });
    }
    throw redirect({ to: "/obras" });
  },
});

// Search tipado de /obras: `elegir` marca la llegada EXPLÍCITA al selector, la de quien lo ha
// pedido desde el conmutador («Todas las obras…»). Viaja en la URL —no en el estado de
// navegación ni en localStorage— para que el enlace se pueda compartir y sobreviva a un
// recargado (#265 D2). El router serializa la query como TEXTO plano (#247), así que llega
// como el string "true".
function obraSelectorSearchSchema(search: Record<string, unknown>): { elegir?: boolean } {
  return { elegir: search.elegir === true || search.elegir === "true" ? true : undefined };
}

// Selector de obra (F7 #157): con >1 obra lista las obras del usuario; con ==1 auto-entra
// (redirect en beforeLoad); con 0 muestra el estado vacío. El admin global ve todas las
// obras que devuelve /me. Vive a nivel appLayout, sin contexto de obra.
// #265: el auto-entrar es un atajo para los destinos DE PASO (la raíz `/`, o una obra
// recordada que ya no vale). Cuando el usuario llega al selector a propósito, el atajo le
// devolvería al panel del que venía sin explicación, así que la llegada explícita se queda
// en el selector aunque tenga una sola obra.
const obraSelectorRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/obras",
  validateSearch: obraSelectorSearchSchema,
  beforeLoad: ({ context, search }) => {
    if (search.elegir) {
      return;
    }
    const [unica] = context.me.obras;
    if (context.me.obras.length === 1) {
      throw redirect({ to: "/obras/$obraId", params: { obraId: String(unica.id_obra) } });
    }
  },
  component: ObraSelectorPage,
});

// Layout por obra (F7 #157): valida el `$obraId` contra el `me` (rehidratado por RouterGate,
// así que funciona igual offline) y deja `{obraId, obra, rolEnObra}` en el contexto para las
// páginas hijas. Un deep-link a una obra ajena (o un id no numérico) redirige a `/obras`.
// Sin componente propio: TanStack renderiza un <Outlet /> por defecto para las páginas hijas.
// Entrar en una obra válida la recuerda (#252) para que la barra, el conmutador y la raíz
// la conserven al salir a una ruta global; un id inválido o ajeno redirige sin recordarlo.
// Solo cuenta la ENTRADA real: con `defaultPreload: "intent"` este beforeLoad también corre
// al precargar (pasar el ratón por el enlace de otra obra en el selector), y precargar no es
// elegir — recordarla ahí secuestraría el menú, el conmutador y la raíz hacia una obra que el
// usuario nunca abrió.
const obraLayoutRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/obras/$obraId",
  beforeLoad: ({ context, params, preload }) => {
    const activa = resolverObraActiva(context.me, params.obraId);
    if (!preload) {
      recordarObraActiva(activa.obraId);
    }
    return activa;
  },
});

// Gestión de la colección de obras (F7 #161), ADMIN global: listado + alta/edición/baja.
// Vive a nivel appLayout (sin contexto de obra: la colección es global). Es el punto de
// entrada del admin a la gestión de miembros/autorizaciones de cada obra.
// Search tipado de /gestion/obras: `nueva` abre el alta de obra al entrar, para el atajo de
// primer arranque desde el estado vacío del selector (#187).
function gestionObrasSearchSchema(search: Record<string, unknown>): { nueva?: boolean } {
  return { nueva: search.nueva === true || search.nueva === "true" ? true : undefined };
}

const gestionObrasRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/gestion/obras",
  validateSearch: gestionObrasSearchSchema,
  beforeLoad: ({ context }) => {
    requireAdmin(context.me);
  },
  component: GestionObrasPage,
});

// Configuración de una obra (#238 D9, antes «Miembros y autorizaciones»): alta rápida de
// miembros con su rol y su autorización LOTO, y destinatarios de los avisos. Toda la página
// gestiona PERSONAS, así que el guard es canManageObra (ADMIN global o ADMIN_OBRA de la obra,
// espejo de RequireObraAdmin, #255 D2): el CUALIFICADO —que gestiona la fábrica pero no a las
// personas—, el AUTORIZADO y el CONSULTA se redirigen a la propia obra. El backend es el
// guardián real (403 PERMISO_INSUFICIENTE).
const configuracionObraRoute = createRoute({
  getParentRoute: () => obraLayoutRoute,
  path: "/configuracion",
  beforeLoad: ({ context, params }) => {
    if (!canManageObra(context.me, Number(params.obraId))) {
      throw redirect({ to: "/obras/$obraId", params: { obraId: params.obraId } });
    }
  },
  component: ConfiguracionObraPage,
});

const dashboardRoute = createRoute({
  getParentRoute: () => obraLayoutRoute,
  path: "/",
  component: DashboardPage,
});

const areasRoute = createRoute({
  getParentRoute: () => obraLayoutRoute,
  path: "/areas",
  validateSearch: validadorDeFiltros(CLAVES_AREAS),
  component: AreasPage,
});

const candadosRoute = createRoute({
  getParentRoute: () => obraLayoutRoute,
  path: "/candados",
  validateSearch: validadorDeFiltros(CLAVES_CANDADOS),
  component: CandadosPage,
});

const equiposRoute = createRoute({
  getParentRoute: () => obraLayoutRoute,
  path: "/equipos",
  validateSearch: validadorDeFiltros(CLAVES_EQUIPOS),
  component: EquiposPage,
});

// Índice de LOTOs y su ficha (/obras/$obraId/lotos/$id). La operación LOTO (F3) es visible a
// cualquier usuario onboarded (incluida CONSULTA, solo lectura); las mutaciones las guarda el
// backend por la regla puede_registrar de la obra, y la UI oculta las acciones a quien no lo cumple.
const lotosRoute = createRoute({
  getParentRoute: () => obraLayoutRoute,
  path: "/lotos",
  validateSearch: validadorDeFiltros(CLAVES_LOTOS),
  component: LotosPage,
});

const lotoDetailRoute = createRoute({
  getParentRoute: () => obraLayoutRoute,
  path: "/lotos/$id",
  component: LotoDetailPage,
});

const fuentesRoute = createRoute({
  getParentRoute: () => obraLayoutRoute,
  path: "/fuentes",
  validateSearch: validadorDeFiltros(CLAVES_FUENTES),
  component: FuentesPage,
});

const procedimientosRoute = createRoute({
  getParentRoute: () => obraLayoutRoute,
  path: "/procedimientos",
  validateSearch: validadorDeFiltros(CLAVES_PROCEDIMIENTOS),
  component: ProcedimientosPage,
});

const puntosRoute = createRoute({
  getParentRoute: () => obraLayoutRoute,
  path: "/puntos",
  validateSearch: validadorDeFiltros(CLAVES_PUNTOS),
  component: PuntosPage,
});

// Informes de LOTOs con gráficas (#215): vista de solo lectura, visible para cualquier miembro
// onboarded de la obra (también CONSULTA), como el panel. Cuelga de la obra activa.
const informesRoute = createRoute({
  getParentRoute: () => obraLayoutRoute,
  path: "/informes",
  component: InformesPage,
});

// Centro de sincronización (F4 #53): bandeja del trabajo hecho sin conexión pendiente de confirmar
// con el servidor (pendientes de enviar + conflictos por resolver). Abierta a cualquier onboarded
// (la outbox es del propio dispositivo); se alcanza desde el indicador de la cabecera.
const sincronizacionRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/sincronizacion",
  component: ConflictCenterPage,
});

// La gestión de usuarios es ADMIN-only (RequireAdmin en el backend): el guard de
// ruta redirige a la raíz a quien no sea ADMIN, además de ocultarse en la nav. El
// `me` lo resuelve el beforeLoad de appLayoutRoute y llega por el contexto.
const usuariosRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/usuarios",
  validateSearch: validadorDeFiltros(CLAVES_USUARIOS),
  beforeLoad: ({ context }) => {
    requireAdmin(context.me);
  },
  component: UsuariosPage,
});

// El visor de auditoría es ADMIN-only y GLOBAL (#254, D1): el registro cruza todas las
// obras y contiene datos personales, así que cuelga de appLayoutRoute (no de la obra) con
// el mismo guard que la gestión de usuarios. El filtro por obra viaja como parámetro de la
// consulta, no como segmento de ruta.
const auditoriaRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/auditoria",
  beforeLoad: ({ context }) => {
    requireAdmin(context.me);
  },
  component: AuditoriaPage,
});

const changePasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/mi-clave",
  beforeLoad: async ({ context, location }) => {
    // Alcanzable autenticado aunque debe_cambiar_password sea true: es la pantalla
    // que salda ese cambio (y también sirve para el cambio voluntario).
    const me = await ensureAuth(context.queryClient, location.href);
    return { me };
  },
  component: ChangePasswordPage,
});

const ndaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/nda",
  beforeLoad: async ({ context, location }) => {
    const me = await ensureAuth(context.queryClient, location.href);
    // Orden de onboarding: la contraseña va antes que el NDA.
    if (me.debe_cambiar_password) {
      throw redirect({ to: "/mi-clave" });
    }
    return { me };
  },
  component: NdaPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  recoverRoute,
  invitacionRoute,
  resetRoute,
  appLayoutRoute.addChildren([
    indexRoute,
    obraSelectorRoute,
    obraLayoutRoute.addChildren([
      dashboardRoute,
      areasRoute,
      candadosRoute,
      equiposRoute,
      fuentesRoute,
      lotosRoute,
      lotoDetailRoute,
      procedimientosRoute,
      puntosRoute,
      informesRoute,
      configuracionObraRoute,
    ]),
    gestionObrasRoute,
    sincronizacionRoute,
    usuariosRoute,
    auditoriaRoute,
  ]),
  changePasswordRoute,
  ndaRoute,
]);

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  scrollRestoration: true,
  // La query de la URL es TEXTO plano, sin JSON (#247): los parámetros de los filtros son
  // los MISMOS que los del API (`?area=3&estado=BLOQUEADO`), de modo que una URL se puede
  // compartir, teclear a mano y pegar en un `curl`. Con el serializador por defecto de
  // TanStack, un `"3"` se escribiría `%223%22` y un `3` tecleado llegaría como número: dos
  // formas distintas de lo mismo. Todos los `validateSearch` del router ya leen texto.
  parseSearch: parseSearchWith((valor) => valor),
  stringifySearch: stringifySearchWith((valor) => String(valor)),
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
